/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderNavigationService, FilterNames, FilterRestService } from '@gh/builder-shared';
import { AelEvents, getAelEventsArray, JobStatus } from '@gh/builder-shared-data';
import {
  AppRouter,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData, NumberTypes,
  UserContext,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService, NotificationService } from '@gh/core-ui';
import {
  buildNumberColumn,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';

import { JobListFilter, JobListItem } from '../../resources/job.model';
import { JobCoJobListBusinessService } from '../../resources/job-co-job-list-business.service';
import { JobNavigationService } from '../../resources/job-navigation.service';
import { concat } from 'lodash';

@Component({
  standalone: false,
             selector: 'gh-job-co-job-list',
             templateUrl: 'job-co-job-list.component.html',
           })
export class JobCoJobListComponent implements OnInit {
  dataSource: SlicedDataSource<JobListItem>;
  form: FilterableFormState<JobListFilter>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: UserContext,
              private formStateBuilder: FormStateBuilder,
              private businessService: JobCoJobListBusinessService,
              private dialogs: DialogService,
              private jobNavigation: JobNavigationService,
              private notifications: NotificationService,
              private router: AppRouter,
              private filterService: FilterRestService,
              private navigationService: BuilderNavigationService,
              public gridDefinitionFactory: GridDefinitionFactory,
  ) {

  }

  isJobDpp(job: JobListItem): boolean {
    return job.isDpp;
  }

  ngOnInit(): void {
    const {filter: initFilter, gridLayout } = getRouteInitData(this.route);
    let isInit = true;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        jobNumber: {
          // list always gets sorted from BE
          comparator: () => 0,
        },
        status: {
          minWidth: 120,
          cellClass: ({ data }: { data: any }) => ({
            [JobStatus.Dpp]: 'cell--important',
            [JobStatus.NotProceeding]: 'cell--negative',
            [JobStatus.Active]: 'cell--positive',
            [JobStatus.Finalized]: 'finalized-status',
          }[data.statusValue]),
        },
        outstandingVariations: {
          cellClass: 'text-center',
        },
        variationsAmountWithGst: buildNumberColumn(NumberTypes.Price),
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowId: (params) => String(params.data.id)
      });
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: initFilter,
      children: {
        showActive: void 0,
        showUserAssignedJobs: void 0,
        searchString: void 0,
        showFinalized: void 0,
        showNotProceeding: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.dataSource = this.businessService.findAllAsDataSource({
        ...filter,
      });
      if (!isInit) {
        this.filterService.update(FilterNames.jobCoList, filter).subscribe();
      }
      isInit = false;
    });
  }

  onJobDetailsOpen(job: JobListItem): void {
    this.jobNavigation.onJobDetailsOpen(job, 'lbl_job_change_orders');
  }

  goToAel(job?: JobListItem): void {
    this.navigationService.goToAel({
      label: 'lbl_job_change_orders',
      entityType: 'JOB',
      entityId: job ? job.id : undefined,
      eventTypeIds: concat(getAelEventsArray(AelEvents.ChangeOrder),
                           getAelEventsArray(AelEvents.Job.ChangeOrder),
                           197, 226, getAelEventsArray(AelEvents.Job.ChangeOrderDraw)),
     });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onJobSpecificationOpen(job: JobListItem): void {
    this.jobNavigation.onJobSpecificationOpen(job, 'lbl_job_change_orders');
  }

  onColorSelectionOpen(job: JobListItem): void {
    this.jobNavigation.onColorSelectionOpen(job, 'lbl_job_change_orders');
  }

  onJobEstimateOpen(job: JobListItem): void {
    this.jobNavigation.onJobEstimateOpen(job, 'lbl_job_change_orders');
  }

  onFinancialsOpen(job: JobListItem): void {
    this.jobNavigation.onFinancialsOpen(job, 'lbl_job_change_orders');
  }

  onJobScheduleOpen(): void {
    // not implemented yet
  }

  onJobOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobOrderOpen(job, 'lbl_job_change_orders');
  }

  onJobChangeOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobChangeOrderOpen(job, 'lbl_job_change_orders');
  }
}
